import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { cn } from "~/lib/utils";

type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Una o dos líneas. Si hace falta más, el diálogo no es el lugar. */
  description: string;
  /** Texto del botón que ejecuta la acción. Un verbo, no "Aceptar". */
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  /** La acción destruye algo (borra, cierra, reinicia): el botón va en rojo. */
  destructive?: boolean;
  pending?: boolean;
  pendingLabel?: string;
  /** Para diálogos que viven adentro del reproductor, que ya usa z-index altos. */
  overlayClassName?: string;
  contentClassName?: string;
};

/**
 * EL diálogo de confirmación. Uno solo, para que no vuelvan a divergir.
 *
 * Antes había dos, cada uno con su forma: el de reiniciar la clase usaba botones tipo píldora y
 * ponía la acción principal arriba en teléfono; el de cerrar sesión traía un antetítulo, un
 * ícono en recuadro, un título de 34px, un cartelito verde de "tu progreso está a salvo", y
 * —lo peor— pintaba CANCELAR como el botón principal, oscuro y grande, mientras que confirmar
 * quedaba como un texto suelto. Uno leía "cerrar sesión" y el botón que saltaba a la vista era
 * el que NO cerraba sesión.
 *
 * Las reglas que fija este componente:
 *
 *  · Título, una explicación corta, dos botones. Nada más. Un diálogo de confirmación se lee en
 *    dos segundos con el dedo ya en el aire; todo lo demás compite con la decisión.
 *  · La acción que se confirma es SIEMPRE el botón con peso visual, y el cancelar es el
 *    secundario. Nunca al revés.
 *  · Mismo orden en teléfono y en escritorio: cancelar primero, confirmar después. Apilados,
 *    confirmar queda abajo —donde cae el pulgar—; en fila, a la derecha. En los dos casos es lo
 *    último que se lee, que es lo que hace que el orden se sienta igual aunque cambie el eje.
 *  · A ancho completo en teléfono: dos botones angostos uno al lado del otro se erran.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancelar",
  onConfirm,
  destructive = false,
  pending = false,
  pendingLabel,
  overlayClassName,
  contentClassName,
}: ConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        overlayClassName={cn("bg-landing-ink-950/55 backdrop-blur-[6px]", overlayClassName)}
        className={cn(
          "max-w-[calc(100vw-28px)] gap-0 rounded-2xl border border-landing-border bg-landing-white p-5 text-landing-text shadow-[0_28px_90px_rgba(8,8,12,0.42)] sm:max-w-[420px] sm:p-6",
          contentClassName,
        )}
      >
        <AlertDialogHeader className="space-y-0 text-left">
          <AlertDialogTitle className="font-display text-[21px] font-semibold leading-[1.2] tracking-[-0.01em] text-landing-stone-900">
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="mt-2 font-landing text-[15px] leading-relaxed text-landing-stone-600">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* `flex-col` sin `-reverse`: confirmar es el último elemento del DOM y queda abajo en
            teléfono y a la derecha en escritorio. El mismo lugar relativo en los dos ejes. */}
        <AlertDialogFooter className="mt-6 flex-col gap-2.5 sm:flex-row sm:justify-end sm:gap-3 sm:space-x-0">
          <AlertDialogCancel className="mt-0 h-11 w-full rounded-lg border-landing-stone-300 bg-landing-white font-landing text-[15px] font-semibold text-landing-stone-800 hover:bg-landing-stone-100 sm:h-10 sm:w-auto sm:px-5 sm:text-sm">
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={pending}
            className={cn(
              "h-11 w-full rounded-lg font-landing text-[15px] font-semibold text-white sm:h-10 sm:w-auto sm:px-5 sm:text-sm",
              destructive
                ? "bg-landing-red-400 hover:bg-landing-red-500"
                : "bg-landing-ink-900 hover:bg-[#342D27]",
            )}
          >
            {pending && pendingLabel ? pendingLabel : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
